import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  RefreshCw, 
  Smartphone, 
  Check, 
  AlertCircle,
  Wifi
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Bouton Admin pour forcer la synchronisation de toutes les applications FDM.
 * 
 * Fonctionnement :
 * - Incrémente la version de l'app côté serveur
 * - Toutes les applications FDM détecteront cette nouvelle version dans les 30 min max
 * - Les applications se mettent à jour automatiquement
 */
const ForceSyncButton = ({ variant = 'full' }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [versionInfo, setVersionInfo] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  const fetchVersionInfo = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/version-info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setVersionInfo(data);
      }
    } catch (error) {
      console.error('Erreur récupération version:', error);
    }
  }, [token]);

  // Récupérer les infos de version au chargement
  useEffect(() => {
    fetchVersionInfo();
  }, [fetchVersionInfo]);

  const handleForceSync = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/bump-version`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur serveur');
      }
      
      const data = await response.json();
      
      // Afficher le succès
      setShowSuccess(true);
      toast.success(
        `Mise à jour forcée ! Version ${data.new_version}`,
        { 
          description: 'Toutes les applications se mettront à jour sous 30 min max.',
          duration: 5000
        }
      );
      
      // Rafraîchir les infos
      await fetchVersionInfo();
      
      // Cacher le succès après 3 secondes
      setTimeout(() => setShowSuccess(false), 3000);
      
    } catch (error) {
      console.error('Erreur forçage MAJ:', error);
      toast.error('Erreur lors du forçage de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  // Version compacte (juste un bouton)
  if (variant === 'compact') {
    return (
      <Button
        onClick={handleForceSync}
        disabled={loading}
        variant="outline"
        size="sm"
        className={`gap-2 ${showSuccess ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : ''}`}
      >
        {loading ? (
          <RefreshCw className="h-4 w-4 animate-spin" />
        ) : showSuccess ? (
          <Check className="h-4 w-4" />
        ) : (
          <Wifi className="h-4 w-4" />
        )}
        {showSuccess ? 'MAJ envoyée !' : 'Forcer MAJ'}
      </Button>
    );
  }

  // Version complète (avec infos)
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
          showSuccess 
            ? 'bg-emerald-100 text-emerald-600' 
            : 'bg-blue-100 text-blue-600'
        }`}>
          {showSuccess ? (
            <Check className="h-5 w-5" />
          ) : (
            <Smartphone className="h-5 w-5" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800 text-sm">
            Synchronisation des applications
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {versionInfo ? (
              <>Version actuelle : <span className="font-medium">{versionInfo.version}</span></>
            ) : (
              'Chargement...'
            )}
          </p>
          {versionInfo?.updated_by_name && (
            <p className="text-xs text-slate-400 mt-0.5">
              Dernière MAJ par {versionInfo.updated_by_name}
            </p>
          )}
        </div>
        
        <Button
          onClick={handleForceSync}
          disabled={loading}
          size="sm"
          className={`shrink-0 ${
            showSuccess 
              ? 'bg-emerald-500 hover:bg-emerald-600' 
              : 'bg-blue-600 hover:bg-blue-700'
          }`}
        >
          {loading ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              Envoi...
            </>
          ) : showSuccess ? (
            <>
              <Check className="h-4 w-4 mr-2" />
              Envoyé !
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Forcer MAJ
            </>
          )}
        </Button>
      </div>
      
      {/* Info sur le fonctionnement */}
      <div className="mt-3 p-2 bg-slate-50 rounded-lg">
        <p className="text-xs text-slate-500 flex items-start gap-2">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-blue-500" />
          <span>
            Les applications FDM vérifient automatiquement les mises à jour toutes les 30 minutes. 
            Cliquez sur "Forcer MAJ" après un déploiement pour que tous les téléphones se mettent à jour.
          </span>
        </p>
      </div>
    </div>
  );
};

export default ForceSyncButton;
